import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightLeft,
  BarChart3,
  BedDouble,
  CalendarDays,
  Copy,
  Crown,
  FileBarChart,
  Filter,
  Globe,
  LogIn,
  LogOut,
  MessageSquare,
  Moon,
  Pencil,
  Plus,
  Search,
  Settings2,
  Star,
  Trash2,
  Tv,
  UserRound,
  X,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { api, type Device, type Room } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { Empty, ErrorBox, Field, Loading, Modal, PageHeader, Toggle, timeAgo } from '../components/ui';
import { Select } from '../components/Select';
import { Pager, usePaged } from '../components/Pager';
import { ask } from '../components/Confirm';

export interface Stay {
  id: string;
  roomId: string;
  room: { id: string; number: string };
  status: 'IN_HOUSE' | 'CHECKED_OUT';
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  language: string | null;
  vip: boolean;
  reservationId: string | null;
  checkInAt: string;
  expectedCheckOut: string | null;
  checkOutAt: string | null;
  source: string;
  displayName: string | null;
}

const LANGUAGES = [
  ['', '—'],
  ['en', 'English'],
  ['ar', 'Arabic'],
  ['fr', 'French'],
  ['de', 'German'],
  ['es', 'Spanish'],
  ['it', 'Italian'],
  ['ru', 'Russian'],
  ['zh', 'Chinese'],
  ['tr', 'Turkish'],
] as const;
const LANG_NAME = Object.fromEntries(LANGUAGES) as Record<string, string>;

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString([], { dateStyle: 'medium' }) : '—');
const fmtTime = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
const sourceOf = (s: Stay) => s.source.replace(/^MANUAL:.*|^MANUAL$/, 'Front desk').replace(/^PMS:/, 'PMS · ');
const initials = (s: Stay) => ([s.firstName?.[0], s.lastName?.[0]].filter(Boolean).join('') || s.room.number.slice(0, 2)).toUpperCase();
const nights = (from: string, to: string | null) => (to ? Math.max(0, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000)) : null);
/** ISO → value for <input type="datetime-local"> (local time). */
export const toLocalInput = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};
export const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);

export const useRooms = (siteId: string) =>
  useQuery({ queryKey: ['rooms', siteId, null], queryFn: () => api.get<{ items: Room[] }>(`/sites/${siteId}/rooms`) });

function StatCard({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: number; label: string; sub: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-sub">{sub}</span>
      </span>
    </div>
  );
}

/** Front desk: who is in which room. Check-in puts the guest name on the TV; check-out resets the room. */
export function GuestsPage() {
  const site = useSite();
  const { can } = useSession();
  const [modal, setModal] = useState<null | 'checkin' | 'message'>(null);
  return (
    <>
      <PageHeader
        title="Guests"
        sub="Check-in shows the guest's name on the TV. Check-out clears the room: wake-up calls, messages, favourites and watch history."
        actions={
          site &&
          can('OPERATOR') && (
            <>
              <button className="btn" onClick={() => setModal('message')}>
                <MessageSquare size={16} /> Send message
              </button>
              <button className="btn primary" onClick={() => setModal('checkin')}>
                <LogIn size={16} /> Check in
              </button>
            </>
          )
        }
      />
      <NeedsSite>{site && <StayList key={site.id} siteId={site.id} />}</NeedsSite>
      {modal === 'checkin' && site && <StayModal siteId={site.id} onClose={() => setModal(null)} />}
      {modal === 'message' && site && <SendMessageModal siteId={site.id} onClose={() => setModal(null)} />}
    </>
  );
}

interface DirectMessage {
  id: string;
  audience: { roomIds?: string[] };
  createdAt: string;
}

function StayList({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const [tab, setTab] = useState<'IN_HOUSE' | 'CHECKED_OUT'>('IN_HOUSE');
  const [q, setQ] = useState('');
  const [lang, setLang] = useState('all');
  const [vip, setVip] = useState('all');
  const [src, setSrc] = useState('all');
  const [sort, setSort] = useState<{ key: 'room' | 'guest'; dir: 1 | -1 }>({ key: 'room', dir: 1 });
  const [selId, setSelId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<Stay | null>(null);
  const [moving, setMoving] = useState<Stay | null>(null);
  const [messaging, setMessaging] = useState<string[] | null>(null);
  const inHouse = useQuery({ queryKey: ['stays', siteId, 'IN_HOUSE'], queryFn: () => api.get<{ items: Stay[] }>(`/sites/${siteId}/stays?status=IN_HOUSE`), refetchInterval: 30_000 });
  const out = useQuery({ queryKey: ['stays', siteId, 'CHECKED_OUT'], queryFn: () => api.get<{ items: Stay[] }>(`/sites/${siteId}/stays?status=CHECKED_OUT`), refetchInterval: 60_000 });
  const devices = useQuery({ queryKey: ['devices', siteId], queryFn: () => api.get<{ items: Device[] }>(`/sites/${siteId}/devices`) });
  const direct = useQuery({ queryKey: ['messages', siteId, 'direct'], queryFn: () => api.get<{ items: DirectMessage[] }>(`/sites/${siteId}/messages?view=direct`) });
  const checkOut = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await api.post(`/stays/${id}/check-out`);
    },
    onSuccess: () => {
      setChecked(new Set());
      setSelId(null);
      return qc.invalidateQueries({ queryKey: ['stays', siteId] });
    },
  });
  const ask1 = (s: Stay) => ask(`Check out room ${s.room.number}? The TV is reset for the next guest: wake-up calls, messages, favourites and watch history are cleared.`, () => checkOut.mutate([s.id]), { action: 'Check out' });

  const ins = inHouse.data?.items ?? [];
  const outs = out.data?.items ?? [];
  const today = new Date().toDateString();
  const all = tab === 'IN_HOUSE' ? ins : outs;
  const langs = [...new Set(all.map((s) => s.language).filter(Boolean) as string[])].sort();
  const sources = [...new Set(all.map(sourceOf))].sort();
  const filtering = !!q || lang !== 'all' || vip !== 'all' || src !== 'all';
  const items = all
    .filter((s) => !q || [s.room.number, s.displayName, s.reservationId].some((v) => v?.toLowerCase().includes(q.toLowerCase())))
    .filter((s) => lang === 'all' || (s.language ?? '') === lang)
    .filter((s) => vip === 'all' || (vip === 'vip' ? s.vip : !s.vip))
    .filter((s) => src === 'all' || sourceOf(s) === src)
    .sort((a, b) =>
      sort.key === 'guest'
        ? (a.lastName ?? a.displayName ?? '').localeCompare(b.lastName ?? b.displayName ?? '') * sort.dir
        : a.room.number.localeCompare(b.room.number, undefined, { numeric: true }) * sort.dir,
    );
  const { rows: paged, pager } = usePaged(items);
  const sel = all.find((s) => s.id === selId) ?? null;
  const pageIds = paged.map((s) => s.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => checked.has(id));
  const operator = can('OPERATOR');
  const sortBy = (key: 'room' | 'guest') => setSort((c) => ({ key, dir: c.key === key ? (-c.dir as 1 | -1) : 1 }));

  return (
    <>
      <div className="stat-strip">
        <StatCard icon={<UserRound size={22} />} tone="violet" value={ins.length} label="In-house guests" sub="Currently checked in" />
        <StatCard icon={<LogOut size={22} />} tone="blue" value={outs.filter((s) => s.checkOutAt && new Date(s.checkOutAt).toDateString() === today).length} label="Checked out today" sub="Rooms cleared today" />
        <StatCard icon={<Crown size={22} />} tone="amber" value={ins.filter((s) => s.vip).length} label="VIP guests" sub="Currently in house" />
        <StatCard icon={<BedDouble size={22} />} tone="violet" value={new Set(ins.map((s) => s.roomId)).size} label="Active rooms" sub="With guests" />
      </div>

      <div className={`fac-layout guests-layout${sel ? '' : ' no-detail'}`}>
        <div className="card fill">
          <div className="row" style={{ gap: 8 }}>
            {(
              [
                ['IN_HOUSE', `In house (${ins.length})`],
                ['CHECKED_OUT', `Checked out (${outs.length})`],
              ] as const
            ).map(([k, l]) => (
              <button
                key={k}
                className={`pill-tab${tab === k ? ' on' : ''}`}
                onClick={() => {
                  setTab(k);
                  setChecked(new Set());
                  setSelId(null);
                  setLang('all');
                  setSrc('all');
                }}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="filters" style={{ marginTop: 14 }}>
            <label className="search">
              <Search size={16} />
              <input placeholder="Search room, guest, reservation…" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Select value={lang} onChange={(e) => setLang(e.target.value)} aria-label="Language">
              <option value="all">All languages</option>
              {langs.map((l) => (
                <option key={l} value={l}>
                  {LANG_NAME[l] ?? l}
                </option>
              ))}
            </Select>
            <Select value={vip} onChange={(e) => setVip(e.target.value)} aria-label="Guests">
              <option value="all">All guests</option>
              <option value="vip">VIP only</option>
              <option value="regular">Non-VIP</option>
            </Select>
            <Select value={src} onChange={(e) => setSrc(e.target.value)} aria-label="Source">
              <option value="all">All sources</option>
              {sources.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
            {filtering && (
              <button
                className="btn-link"
                onClick={() => {
                  setQ('');
                  setLang('all');
                  setVip('all');
                  setSrc('all');
                }}
              >
                <Filter size={14} /> Clear filters
              </button>
            )}
          </div>
          {checked.size > 0 && operator && tab === 'IN_HOUSE' && (
            <div className="bulk-bar">
              <strong>{checked.size} selected</strong>
              <button className="btn small" onClick={() => setMessaging(ins.filter((s) => checked.has(s.id)).map((s) => s.roomId))}>
                <MessageSquare size={14} /> Send message
              </button>
              <button
                className="btn small danger"
                onClick={() => ask(`Check out ${checked.size} rooms? Their TVs are reset for the next guests.`, () => checkOut.mutate([...checked]), { action: 'Check out' })}
              >
                <LogOut size={14} /> Check out
              </button>
              <button className="icon-btn" style={{ marginLeft: 'auto' }} onClick={() => setChecked(new Set())} title="Clear selection">
                <X size={16} />
              </button>
            </div>
          )}
          {inHouse.isLoading || out.isLoading ? (
            <Loading />
          ) : items.length === 0 ? (
            <Empty>{all.length ? 'No guest matches.' : tab === 'IN_HOUSE' ? 'No guests checked in. Use “Check in”, or connect your PMS in Integrations.' : 'No recent check-outs.'}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="fac-table">
                <thead>
                  <tr>
                    {operator && tab === 'IN_HOUSE' && (
                      <th style={{ width: 34 }}>
                        <input
                          type="checkbox"
                          checked={allOnPage}
                          aria-label="Select this page"
                          onChange={() =>
                            setChecked((c) => {
                              const n = new Set(c);
                              pageIds.forEach((id) => (allOnPage ? n.delete(id) : n.add(id)));
                              return n;
                            })
                          }
                        />
                      </th>
                    )}
                    <th className="sortable" onClick={() => sortBy('room')}>
                      Room
                    </th>
                    <th className="sortable" onClick={() => sortBy('guest')}>
                      Guest
                    </th>
                    <th>Language</th>
                    <th>Reservation</th>
                    <th>Checked in</th>
                    <th>{tab === 'IN_HOUSE' ? 'Expected check-out' : 'Checked out'}</th>
                    <th>Source</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((s) => {
                    const end = tab === 'IN_HOUSE' ? s.expectedCheckOut : s.checkOutAt;
                    return (
                      <tr key={s.id} className={sel?.id === s.id ? 'selected' : ''} onClick={() => setSelId(s.id)}>
                        {operator && tab === 'IN_HOUSE' && (
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={checked.has(s.id)}
                              aria-label={`Select room ${s.room.number}`}
                              onChange={() =>
                                setChecked((c) => {
                                  const n = new Set(c);
                                  if (n.has(s.id)) n.delete(s.id);
                                  else n.add(s.id);
                                  return n;
                                })
                              }
                            />
                          </td>
                        )}
                        <td>
                          <strong>{s.room.number}</strong>
                        </td>
                        <td>
                          {s.displayName ?? <span className="muted">—</span>}
                          {s.vip && (
                            <span className="badge brand vip-badge">
                              <Star size={11} /> VIP
                            </span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {s.language ? (
                            <>
                              <span className="lang-badge">{s.language}</span> {LANG_NAME[s.language] ?? ''}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="mono">{s.reservationId ?? '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {timeAgo(s.checkInAt)}
                          <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                            {fmtDate(s.checkInAt)}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {fmtDate(end)}
                          <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                            {fmtTime(end)}
                          </span>
                        </td>
                        <td className="muted">{sourceOf(s)}</td>
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                          {tab === 'IN_HOUSE' && operator && (
                            <>
                              <button className="row-btn" title="Send message" onClick={() => setMessaging([s.roomId])}>
                                <MessageSquare size={15} />
                              </button>
                              <button className="row-btn" title="Edit stay" onClick={() => setEditing(s)}>
                                <Pencil size={15} />
                              </button>
                              <button className="row-btn danger-icon" title="Check out" disabled={checkOut.isPending} onClick={() => ask1(s)}>
                                <LogOut size={15} />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <Pager {...pager} />
          <ErrorBox error={inHouse.error ?? out.error ?? checkOut.error} />
        </div>

        {sel && (
          <GuestPanel
            s={sel}
            tvs={(devices.data?.items ?? []).filter((d) => d.roomId === sel.roomId)}
            messages={(direct.data?.items ?? []).filter((m) => m.audience.roomIds?.includes(sel.roomId) && m.createdAt >= sel.checkInAt).length}
            operator={operator}
            onClose={() => setSelId(null)}
            onEdit={() => setEditing(sel)}
            onMove={() => setMoving(sel)}
            onMessage={() => setMessaging([sel.roomId])}
            onCheckOut={() => ask1(sel)}
          />
        )}
      </div>
      {editing && <StayModal siteId={siteId} stay={editing} onClose={() => setEditing(null)} />}
      {moving && <MoveModal siteId={siteId} stay={moving} onClose={() => setMoving(null)} />}
      {messaging && <SendMessageModal siteId={siteId} roomIds={messaging} onClose={() => setMessaging(null)} />}
    </>
  );
}

function GuestPanel({
  s,
  tvs,
  messages,
  operator,
  onClose,
  onEdit,
  onMove,
  onMessage,
  onCheckOut,
}: {
  s: Stay;
  tvs: Device[];
  messages: number;
  operator: boolean;
  onClose: () => void;
  onEdit: () => void;
  onMove: () => void;
  onMessage: () => void;
  onCheckOut: () => void;
}) {
  const inHouse = s.status === 'IN_HOUSE';
  const n = nights(s.checkInAt, inHouse ? s.expectedCheckOut : s.checkOutAt);
  const left = inHouse && s.expectedCheckOut ? Math.max(0, Math.ceil((new Date(s.expectedCheckOut).getTime() - Date.now()) / 86_400_000)) : null;
  const row = (label: string, value: ReactNode) => (
    <div className="stay-row">
      <span className="muted">{label}</span>
      <span>{value}</span>
    </div>
  );
  return (
    <div className="card fac-detail guest-panel">
      <div className="row" style={{ gap: 12, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
        <span className="guest-avatar">{initials(s)}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="row" style={{ gap: 6 }}>
            <strong style={{ fontSize: 17 }}>{s.displayName ?? 'Guest'}</strong>
            {s.vip && (
              <span className="badge brand">
                <Star size={11} /> VIP
              </span>
            )}
          </span>
          <span className="row" style={{ gap: 8, marginTop: 4 }}>
            <span>Room {s.room.number}</span>
            <span className={`badge ${inHouse ? 'ok' : ''}`}>{inHouse ? 'In house' : 'Checked out'}</span>
          </span>
        </span>
        <button className="icon-btn" onClick={onClose} title="Close">
          <X size={16} />
        </button>
      </div>

      <h4 className="panel-h">
        <CalendarDays size={17} /> Stay information
      </h4>
      <div className="stay-box">
        {row('Reservation', s.reservationId ?? '—')}
        {row('Language', s.language ? <><span className="lang-badge">{s.language}</span> {LANG_NAME[s.language] ?? s.language}</> : '—')}
        {row('Source', sourceOf(s))}
        {row('Checked in', `${fmt(s.checkInAt)} (${timeAgo(s.checkInAt)})`)}
        {row(inHouse ? 'Expected check-out' : 'Checked out', fmt(inHouse ? s.expectedCheckOut : s.checkOutAt))}
      </div>

      <h4 className="panel-h">
        <BarChart3 size={17} /> Stay at a glance
      </h4>
      <div className="act-grid">
        <div>
          <MessageSquare size={18} className="act-ic violet" />
          <span>
            <span className="muted">Messages sent</span>
            <strong>{messages}</strong>
          </span>
        </div>
        <div>
          <Tv size={18} className="act-ic green" />
          <span>
            <span className="muted">TVs in the room</span>
            <strong>
              {tvs.filter((d) => d.online).length}/{tvs.length} online
            </strong>
          </span>
        </div>
        <div>
          <Moon size={18} className="act-ic blue" />
          <span>
            <span className="muted">Nights</span>
            <strong>{n ?? '—'}</strong>
          </span>
        </div>
        <div>
          <Globe size={18} className="act-ic amber" />
          <span>
            <span className="muted">{inHouse ? 'Days left' : 'Left'}</span>
            <strong>{inHouse ? left ?? '—' : timeAgo(s.checkOutAt ?? s.checkInAt)}</strong>
          </span>
        </div>
      </div>

      <Link
        className="btn"
        style={{ width: '100%', justifyContent: 'center', marginTop: 14 }}
        to={`/devices/report?room=${s.roomId}&from=${encodeURIComponent(new Date(new Date(s.checkInAt).getTime() - 86_400_000).toISOString())}${s.checkOutAt ? `&to=${encodeURIComponent(s.checkOutAt)}` : ''}`}
      >
        <FileBarChart size={15} /> TV report for this stay
      </Link>
      {inHouse && operator && (
        <>
          <h4 className="panel-h">
            <Settings2 size={17} /> Quick actions
          </h4>
          <button className="btn primary" style={{ width: '100%', justifyContent: 'center' }} onClick={onEdit}>
            <Pencil size={15} /> Edit stay
          </button>
          <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'nowrap' }}>
            <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onMessage}>
              <MessageSquare size={15} /> Send message
            </button>
            <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onMove}>
              <ArrowRightLeft size={15} /> Room move
            </button>
          </div>
          <button className="btn danger-outline" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} onClick={onCheckOut}>
            <LogOut size={15} /> Check out and clear the room
          </button>
          <div className="muted" style={{ fontSize: 12, textAlign: 'center', marginTop: 6 }}>
            Clears wake-up calls, messages, favourites and watch history on the TV.
          </div>
        </>
      )}
    </div>
  );
}


function StayModal({ siteId, stay, onClose }: { siteId: string; stay?: Stay; onClose: () => void }) {
  const qc = useQueryClient();
  const rooms = useRooms(siteId);
  const [room, setRoom] = useState(stay?.room.number ?? '');
  const [title, setTitle] = useState(stay?.title ?? '');
  const [firstName, setFirstName] = useState(stay?.firstName ?? '');
  const [lastName, setLastName] = useState(stay?.lastName ?? '');
  const [language, setLanguage] = useState(stay?.language ?? '');
  const [vip, setVip] = useState(stay?.vip ?? false);
  const [checkout, setCheckout] = useState(toLocalInput(stay?.expectedCheckOut));
  const [reservationId, setReservationId] = useState(stay?.reservationId ?? '');
  const guest = { title: title || null, firstName: firstName || null, lastName: lastName || null, language: language || null, vip };
  const save = useMutation({
    mutationFn: () =>
      stay
        ? api.patch(`/stays/${stay.id}`, { ...guest, expectedCheckOut: fromLocalInput(checkout) })
        : api.post(`/sites/${siteId}/stays/check-in`, { room, guest, reservationId: reservationId || null, expectedCheckOut: fromLocalInput(checkout) }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['stays', siteId] });
      onClose();
    },
  });
  return (
    <Modal
      title={stay ? `Guest in room ${stay.room.number}` : 'Check in'}
      onClose={onClose}
      footer={
        <button className="btn primary" disabled={!room || (!firstName && !lastName) || save.isPending} onClick={() => save.mutate()}>
          {stay ? 'Save' : 'Check in'}
        </button>
      }
    >
      {!stay && (
        <Field label="Room" hint="A guest already in this room is checked out first.">
          <Select value={room} onChange={(e) => setRoom(e.target.value)}>
            <option value="">Select a room</option>
            {(rooms.data?.items ?? []).map((r) => (
              <option key={r.id} value={r.number}>
                {r.number}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <div className="grid" style={{ gridTemplateColumns: '110px 1fr 1fr', gap: 12 }}>
        <Field label="Title">
          <Select value={title} onChange={(e) => setTitle(e.target.value)}>
            {['', 'Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof', 'Sheikh', 'Sheikha', 'HE'].map((t) => (
              <option key={t} value={t}>
                {t || '—'}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="First name">
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field label="Last name">
          <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </Field>
      </div>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Language" hint="Targets messages; TV language later.">
          <Select value={language} onChange={(e) => setLanguage(e.target.value)}>
            {LANGUAGES.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Expected check-out">
          <input type="datetime-local" value={checkout} onChange={(e) => setCheckout(e.target.value)} />
        </Field>
      </div>
      {!stay && (
        <Field label="Reservation number" hint="Optional. Lets the PMS check the guest out by reservation.">
          <input value={reservationId} onChange={(e) => setReservationId(e.target.value)} />
        </Field>
      )}
      <Field label="VIP">
        <Toggle on={vip} onChange={setVip} />
      </Field>
      <ErrorBox error={save.error} />
    </Modal>
  );
}

function MoveModal({ siteId, stay, onClose }: { siteId: string; stay: Stay; onClose: () => void }) {
  const qc = useQueryClient();
  const rooms = useRooms(siteId);
  const [to, setTo] = useState('');
  const move = useMutation({
    mutationFn: () => api.post(`/sites/${siteId}/stays/move`, { fromRoom: stay.room.number, toRoom: to }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['stays', siteId] });
      onClose();
    },
  });
  return (
    <Modal
      title={`Move ${stay.displayName ?? 'guest'}`}
      onClose={onClose}
      footer={
        <button className="btn primary" disabled={!to || move.isPending} onClick={() => move.mutate()}>
          Move
        </button>
      }
    >
      <Field label={`From room ${stay.room.number} to`} hint="The guest's messages follow them; wake-up calls in the old room are cleared.">
        <Select value={to} onChange={(e) => setTo(e.target.value)}>
          <option value="">Select a room</option>
          {(rooms.data?.items ?? [])
            .filter((r) => r.id !== stay.roomId)
            .map((r) => (
              <option key={r.id} value={r.number}>
                {r.number}
              </option>
            ))}
        </Select>
      </Field>
      <ErrorBox error={move.error} />
    </Modal>
  );
}

const FORMATS = [
  ['POPUP', 'Pop-up (until the guest closes it)'],
  ['TOAST', 'Toast (small, disappears)'],
  ['BANNER', 'Banner (bottom of the screen)'],
  ['FULLSCREEN', 'Full screen'],
  ['TICKER', 'Scrolling ticker'],
] as const;

/** Front-desk message to one or more rooms, or the whole hotel. Shown once on each TV. */
export function SendMessageModal({ siteId, roomIds: initialRooms, onClose }: { siteId: string; roomIds?: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const rooms = useRooms(siteId);
  const [roomIds, setRoomIds] = useState<string[]>(initialRooms ?? []);
  const [wholeSite, setWholeSite] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [format, setFormat] = useState<string>('POPUP');
  const send = useMutation({
    mutationFn: () => api.post(`/sites/${siteId}/messages/send`, { roomIds: wholeSite ? undefined : roomIds, wholeSite, title: title || null, body, format }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['messages', siteId] });
      onClose();
    },
  });
  const byId = new Map((rooms.data?.items ?? []).map((r) => [r.id, r]));
  return (
    <Modal
      title="Send message to TVs"
      onClose={onClose}
      footer={
        <button className="btn primary" disabled={!body || (!wholeSite && roomIds.length === 0) || send.isPending} onClick={() => send.mutate()}>
          Send now
        </button>
      }
    >
      <Field label="Whole hotel">
        <Toggle on={wholeSite} onChange={setWholeSite} />
      </Field>
      {!wholeSite && (
        <Field label="Rooms">
          <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
            {roomIds.map((id) => (
              <span key={id} className="badge brand">
                {byId.get(id)?.number ?? '…'}
                <button className="icon-btn" style={{ padding: 0 }} onClick={() => setRoomIds(roomIds.filter((x) => x !== id))} aria-label="Remove">
                  <Trash2 size={11} />
                </button>
              </span>
            ))}
            <Select value="" onChange={(e) => e.target.value && setRoomIds([...roomIds, e.target.value])} style={{ width: 160 }}>
              <option value="">+ Add room</option>
              {(rooms.data?.items ?? [])
                .filter((r) => !roomIds.includes(r.id))
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.number}
                  </option>
                ))}
            </Select>
          </div>
        </Field>
      )}
      <Field label="Title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Front desk" />
      </Field>
      <Field label="Message">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} style={{ minHeight: 90 }} placeholder="Your laundry is ready for delivery." />
      </Field>
      <Field label="Display">
        <Select value={format} onChange={(e) => setFormat(e.target.value)}>
          {FORMATS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </Select>
      </Field>
      <ErrorBox error={send.error} />
    </Modal>
  );
}

// ─────────────────────────── Integrations (PMS) ───────────────────────────

interface PmsConnection {
  id: string;
  name: string;
  type: string;
  tokenHint: string;
  enabled: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

export function IntegrationsPage() {
  const site = useSite();
  const { can } = useSession();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ name: string; token: string } | null>(null);
  const conns = useQuery({
    queryKey: ['pms', site?.id],
    enabled: !!site,
    queryFn: () => api.get<{ items: PmsConnection[] }>(`/sites/${site!.id}/pms-connections`),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/pms-connections/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pms', site?.id] }),
  });
  const base = `${window.location.origin}/api/pms/v1`;
  return (
    <>
      <PageHeader
        title="Integrations"
        sub="Connect a PMS (Opera, Protel, Mews… or middleware) to check guests in and out automatically."
        actions={
          site &&
          can('ADMIN') && (
            <button className="btn primary" onClick={() => setCreating(true)}>
              <Plus size={15} /> New PMS key
            </button>
          )
        }
      />
      <NeedsSite>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>PMS connections</h3>
          {conns.isLoading ? (
            <Loading />
          ) : (conns.data?.items ?? []).length === 0 ? (
            <Empty>No PMS connected yet. Create a key and give it to your PMS integrator.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Key</th>
                  <th>Last used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {conns.data!.items.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.type === 'GENERIC_REST' ? 'Generic REST' : c.type}</td>
                    <td className="mono">pms_…{c.tokenHint}</td>
                    <td>{timeAgo(c.lastUsedAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {can('ADMIN') && (
                        <button className="icon-btn" title="Delete key" onClick={() => ask(`Delete “${c.name}”? The PMS will stop working.`, () => remove.mutate(c.id))}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <ErrorBox error={conns.error ?? remove.error} />
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Generic PMS API</h3>
          <p className="muted">
            Send JSON with <span className="mono">Authorization: Bearer &lt;key&gt;</span>. Rooms are matched by number.
          </p>
          <pre className="mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12.5 }}>
            {`POST ${base}/checkin   {"room":"1203","reservationId":"R-1001","guest":{"title":"Mr","firstName":"Ahmed","lastName":"Ben Ali","language":"ar","vip":true},"expectedCheckOut":"2026-10-08T12:00:00Z"}
POST ${base}/checkout  {"room":"1203"}   or   {"reservationId":"R-1001"}
POST ${base}/move      {"fromRoom":"1203","toRoom":"1507"}
POST ${base}/message   {"room":"1203","title":"Airport transfer","text":"Your car arrives at 16:30."}`}
          </pre>
        </div>
      </NeedsSite>
      {creating && site && (
        <CreateKeyModal
          siteId={site.id}
          onClose={() => setCreating(false)}
          onCreated={(c) => {
            setCreating(false);
            setCreated(c);
          }}
        />
      )}
      {created && (
        <Modal title="PMS key created" onClose={() => setCreated(null)} footer={<button className="btn primary" onClick={() => setCreated(null)}>Done</button>}>
          <p>
            Copy the key for <strong>{created.name}</strong> now. It is not shown again.
          </p>
          <div className="row">
            <input readOnly value={created.token} className="mono" style={{ flex: 1 }} onFocus={(e) => e.target.select()} />
            <button className="btn" onClick={() => navigator.clipboard?.writeText(created.token)}>
              <Copy size={14} /> Copy
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

function CreateKeyModal({ siteId, onClose, onCreated }: { siteId: string; onClose: () => void; onCreated: (c: { name: string; token: string }) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api.post<{ name: string; token: string }>(`/sites/${siteId}/pms-connections`, { name }),
    onSuccess: async (c) => {
      await qc.invalidateQueries({ queryKey: ['pms', siteId] });
      onCreated(c);
    },
  });
  return (
    <Modal
      title="New PMS key"
      onClose={onClose}
      footer={
        <button className="btn primary" disabled={!name || create.isPending} onClick={() => create.mutate()}>
          Create
        </button>
      }
    >
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Opera Cloud" />
      </Field>
      <ErrorBox error={create.error} />
    </Modal>
  );
}
